import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { products } from '../data/mock';
import { Package, Search } from 'lucide-react';

export const ProductsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Specialty Hoses', 'Industrial Hoses', 'Pipes'];

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
              <Package size={40} className="text-[#D8461D]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900">
              Our Products
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive selection of industrial hoses and pipes for diverse applications
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-lg font-semibold transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#D8461D] text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <Link key={product.id} to={`/products/${product.id}`}>
                  <Card className="group cursor-pointer transition-all duration-300 hover:shadow-xl border-slate-200 overflow-hidden h-full">
                    <div className="relative overflow-hidden bg-white">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-56 object-contain transition-transform duration-500 group-hover:scale-110 bg-white p-4"
                      />
                      <Badge className="absolute top-4 right-4 bg-[#D8461D] text-white">
                        {product.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6 space-y-3">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D8461D] transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="text-[#D8461D] font-semibold text-sm group-hover:underline pt-2">
                        View Details →
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-slate-600">No products found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-50 to-slate-50 rounded-2xl p-8 md:p-12 border border-orange-100 text-center space-y-6">
            <h3 className="text-3xl font-bold text-slate-900">
              Need a Custom Solution?
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We offer custom manufacturing solutions tailored to your specific requirements. 
              Contact us for bulk orders and specialized products.
            </p>
            <Link to="/contact">
              <button className="px-8 py-3 bg-[#D8461D] hover:bg-#b83817 text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg">
                Request Custom Quote
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
